import { useMutation, useQuery } from '@tanstack/react-query'
import api from '../axios-client'
import { queryClient } from '../query-client'

export const useGetAllDevices = () => {
    return useQuery({
        queryKey: ['devices'],
        queryFn: async () => {
            const response = await api.get('/api/v1/devices')
            return response.data.data
        },
        retry: false,
        staleTime: 1000 * 60 * 2,
        refetchInterval: 1000 * 30,
    })
}

export const useGetBusinessDevices = (businessId: number) =>
    useQuery({
        queryKey: ['devices', 'business', businessId],
        queryFn: async () => {
            const response = await api.get(
                `/api/v1/devices/business/${businessId}`
            )
            return response.data.data
        },
        enabled: !!businessId,
    })

export const useGetDevice = (id: string) => {
    return useQuery({
        queryKey: ['devices', id],
        queryFn: async () => {
            const response = await api.get(`/api/v1/devices/${id}`)
            return response.data.data
        },
        retry: false,
        refetchInterval: 1000 * 60 * 5,
    })
}

export const useGetDeviceErrorCodes = (id: string) => {
    return useQuery({
        queryKey: ['devices', id, 'errorCodes'],
        queryFn: async () => {
            const response = await api.get(`/api/v1/devices/${id}/error-codes`)
            return response.data.data
        },
        retry: false,
        refetchInterval: 1000 * 60 * 5,
    })
}

export const useGetLocationDevice = (id: string, from: string, to: string) => {
    return useQuery({
        queryKey: ['devices', id, 'location', { from, to }],
        queryFn: async () => {
            const response = await api.get(`/api/v1/devices/${id}/location`, {
                params: { from, to },
            })
            return response.data.data
        },
        retry: false,
        staleTime: 1000 * 60 * 60,
    })
}

export const useGetAllDeviceLocations = () => {
    return useQuery({
        queryKey: ['devices', 'all-locations'],
        queryFn: async () => {
            // First get all devices\
            const devicesResponse = await api.get('/api/v1/devices')
            const devices = devicesResponse.data.data

            // Then get the latest location for each device
            const locationPromises = devices.map(async (device: any) => {
                try {
                    const response = await api.get(
                        `/api/v1/devices/${device.id}/location`,
                        {
                            params: { lastOnly: true },
                        }
                    )
                    const locationData = response.data.data

                    if (locationData && locationData.length > 0) {
                        const latestLocation =
                            locationData[locationData.length - 1]
                        return {
                            device,
                            location: latestLocation,
                        }
                    }
                    return {
                        device,
                        location: null,
                    }
                } catch (error) {
                    console.error(
                        `Failed to get location for device ${device.id}:`,
                        error
                    )
                    return {
                        device,
                        location: null,
                    }
                }
            })

            const devicesWithLocations = await Promise.all(locationPromises)
            return devicesWithLocations.filter((item) => item.location !== null)
        },
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
    })
}

export const useGetDeviceHistory = (id: string, from: string, to: string) => {
    return useQuery({
        queryKey: ['devices', id, 'history', { from, to }],
        queryFn: async () => {
            const response = await api.get(`/api/v1/devices/${id}/data`, {
                params: { from, to },
            })
            return response.data
        },
        retry: false,
        // refetchInterval: 1000 * 10,
    })
}

export const useGetDeviceErrors = (id: string) => {
    return useQuery({
        queryKey: ['devices', id, 'errors'],
        queryFn: async () => {
            const response = await api.get(`/api/v1/devices/${id}/errors`)
            return response.data.data
        },
        retry: false,
        staleTime: 1000 * 60 * 60,
    })
}

type Device = {
    name: string
    serial: string
    model: string
    status: string
    business: string
}

export const useCreateDevice = () =>
    useMutation({
        mutationFn: async (data: Device) => {
            const response = await api.post('/api/v1/devices', data)
            return response.data.data
        },
        onSuccess: () => {
            // Invalidate the cache to refetch the data
            queryClient.refetchQueries({
                queryKey: ['devices'],
            })
        },
    })

export const useDeleteDevice = () =>
    useMutation({
        mutationFn: async (id: string) => {
            const response = await api.delete(`/api/v1/devices/${id}`)
            return response.data.data
        },
        onSuccess: () => {
            // Invalidate the cache to refetch the data
            queryClient.refetchQueries({
                queryKey: ['devices'],
            })
        },
    })

export const useGetDeviceConfig = (id: string) => {
    return useQuery({
        queryKey: ['devices', id, 'config'],
        queryFn: async () => {
            const response = await api.get(`/api/v1/devices/${id}/config`)
            return response.data.data
        },
        retry: false,
        staleTime: 1000 * 60 * 60,
    })
}

export const useGetDeviceConfigAttributes = (id: string) => {
    return useQuery({
        queryKey: ['devices', id, 'config', 'attributes'],
        queryFn: async () => {
            const response = await api.get(
                `/api/v1/devices/${id}/config/attributes`
            )
            return response.data.data
        },
        retry: false,
        staleTime: 1000 * 60 * 60,
    })
}

type UseCreateDeviceConfigProps = {
    id: string
    data: object
}

type UseUpdateDeviceConfigProps = {
    id: string
    data: object
}

export const useCreateDeviceConfig = () =>
    useMutation({
        mutationFn: async ({ id, data }: UseCreateDeviceConfigProps) => {
            const response = await api.post(
                `/api/v1/devices/${id}/config`,
                data
            )
            return response.data.data
        },
        onSuccess: () => {
            // Invalidate the cache to refetch the data
            queryClient.refetchQueries({
                queryKey: ['devices', 'config'],
            })
        },
    })

export const useUpdateDeviceConfig = (deviceId: string) => {
    return useMutation({
        mutationFn: (data: UseUpdateDeviceConfigProps) =>
            api.put(`/api/v1/devices/${deviceId}/config/${data.id}`, data.data),
    })
}

export const useGetDeviceDataAroundError = (
    deviceId: string,
    errorId: string,
    options?: {
        window?: number
        enabled?: boolean
    }
) => {
    const defaultOptions = {
        window: 20,
        enabled: true,
        ...options,
    }

    return useQuery({
        queryKey: [
            'deviceDataAroundError',
            deviceId,
            errorId,
            defaultOptions.window,
        ],
        queryFn: async () => {
            const response = await api.get(
                `/api/v1/devices/${deviceId}/errors/${errorId}/context?window=${defaultOptions.window}`
            )

            return response.data.data
        },
        enabled: deviceId != null && errorId != null && defaultOptions.enabled,
    })
}

export const useGetNearbyErrors = (
    deviceId: string,
    errorId: string,
    options?: {
        window?: number
        enabled?: boolean
    }
) => {
    const defaultOptions = {
        window: 60,
        enabled: true,
        ...options,
    }

    return useQuery({
        queryKey: ['nearbyErrors', deviceId, errorId, defaultOptions.window],
        queryFn: async () => {
            const response = await api.get(
                `/api/v1/devices/${deviceId}/errors/${errorId}/nearby?window=${defaultOptions.window}`
            )
            return response.data.data
        },
        enabled: deviceId != null && errorId != null && defaultOptions.enabled,
    })
}

export const useUpdateDevice = () =>
    useMutation({
        mutationFn: async ({
            id,
            data,
        }: {
            id: string
            data: {
                cloudActive?: boolean
                cloudActiveOverride?: boolean
                name?: string
                business?: string
                model?: string
            }
        }) => {
            const response = await api.put(`/api/v1/devices/${id}`, data)
            return response.data.data
        },
        onSuccess: (_, { id }) => {
            // Invalidate the cache to refetch the device data
            queryClient.refetchQueries({
                queryKey: ['devices', id],
            })
            queryClient.refetchQueries({
                queryKey: ['devices'],
            })
        },
    })

export const useSendDeviceCommand = () =>
    useMutation({
        mutationFn: async ({
            id,
            command,
        }: {
            id: string
            command: string
        }) => {
            const response = await api.post(`/api/v1/devices/${id}/command`, {
                command,
            })
            return response.data.data
        },
    })

export const useGetDeviceSoftwareVersions = (id: string) =>
    useQuery({
        queryKey: ['devices', id, 'software-versions'],
        queryFn: async () => {
            const response = await api.get(
                `/api/v1/devices/${id}/software-versions`
            )
            return response.data.data
        },
        enabled: !!id,
    })

export const useGetAvailableSoftwareVersions = (id: string) =>
    useQuery({
        queryKey: ['devices', id, 'software-versions', 'available'],
        queryFn: async () => {
            const response = await api.get(
                `/api/v1/devices/${id}/software-versions/available`
            )
            return response.data.data
        },
        enabled: !!id,
    })

export const useSetDeviceSoftwareVersion = () =>
    useMutation({
        mutationFn: async ({
            deviceId,
            versionId,
        }: {
            deviceId: string
            versionId: number
        }) => {
            const response = await api.put(
                `/api/v1/devices/${deviceId}/software-versions/${versionId}`
            )
            return response.data.data
        },
        onSuccess: (_, { deviceId }) => {
            queryClient.refetchQueries({
                queryKey: ['devices', deviceId, 'software-versions'],
            })
        },
    })
